import { getTokenIdentifier } from '../utils';
import type { AuthFailureDetails } from './types';

/**
 * Options for {@link createAuthFailureHandler}.
 */
export interface CreateAuthFailureHandlerOptions {
  /** App label for the console.error message, e.g. 'Portal' or 'Mobile'. */
  appLabel: string;

  /** Read the current access token, used to derive a short identifier for logging. */
  getToken: () => string | null;

  /** Extra fields merged into the logged failure details (e.g. mobile's email). */
  getDiagnostics?: () => Record<string, unknown>;

  /** Perform the logout with the derived reason string. */
  logout: (reason: string) => void;

  /** Called after logout and before the redirect (e.g. mobile's clearAllCaches). */
  onBeforeLogout?: () => void;
}

/**
 * Creates a `handleAuthFailure` callback shared between portal and mobile.
 *
 * Builds the enhanced failure details (tokenIdentifier, currentRoute, app-specific
 * diagnostics), derives a logout reason from the failure shape, logs the user out,
 * and redirects to /login — except in test environments, where the redirect is
 * skipped to preserve dev tools.
 */
export function createAuthFailureHandler(
  options: CreateAuthFailureHandlerOptions
): (details: AuthFailureDetails) => void {
  const { appLabel, getToken, getDiagnostics, logout, onBeforeLogout } = options;

  return function handleAuthFailure(details: AuthFailureDetails): void {
    const tokenIdentifier = getTokenIdentifier(getToken());

    const enhancedDetails = {
      ...details,
      ...(getDiagnostics?.() ?? {}),
      tokenIdentifier,
      currentRoute: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    };

    console.error(`🔒 ${appLabel} authentication failure - logging out user:`, enhancedDetails);

    let logoutReason = 'auth_failure';
    if (details.refreshFailed) {
      logoutReason = 'token_refresh_failed';
    }
    if (details.noRefreshToken) {
      logoutReason = 'no_refresh_token';
    }
    if (details.tokenExpired) {
      logoutReason = 'token_expired';
    }

    logout(logoutReason);
    onBeforeLogout?.();

    if (import.meta.env.TEST) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  };
}
